import React, { useState, useEffect } from "react";
import {
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import {
  PlanningCycle,
  planningCyclesApi,
  planningConfigApi,
} from "../services/planningCycles";

interface PlanningCycleSelectorProps {
  organizationId: string;
  currentCycleId: string;
  cycles: PlanningCycle[];
  configuredCycleTypes: string[];
  onCycleSelect: (cycleId: string | null) => void;
  onCyclesUpdate?: (cycles: PlanningCycle[]) => void;
  allowAllCycles?: boolean;
}

const PlanningCycleSelector: React.FC<PlanningCycleSelectorProps> = ({
  organizationId,
  currentCycleId,
  cycles,
  configuredCycleTypes,
  onCycleSelect,
  onCyclesUpdate,
  allowAllCycles = false,
}) => {
  const [selectedCycleType, setSelectedCycleType] = useState<string>(
    allowAllCycles ? "all" : "",
  );
  const [selectedCycle, setSelectedCycle] = useState<PlanningCycle | null>(
    null,
  );
  const [creating, setCreating] = useState(false);

  const getCycleTypeLabel = (type: string) => {
    switch (type) {
      case "all":
        return "All Cycles";
      case "30-day":
        return "Monthly";
      case "quarter":
        return "Quarterly";
      case "year":
        return "Annual";
      case "3-year":
        return "3-Year";
      default:
        return type;
    }
  };

  useEffect(() => {
    // Handle "All Cycles" mode
    if (allowAllCycles && !currentCycleId) {
      setSelectedCycleType("all");
      setSelectedCycle(null);
      return;
    }

    // Find the current cycle and set the initial state
    const current = cycles.find((c) => c.id === currentCycleId);
    if (current) {
      setSelectedCycle(current);
      setSelectedCycleType(current.cycle_type);
    } else if (cycles.length > 0 && !allowAllCycles) {
      // Default to the first configured type that has cycles
      for (const type of configuredCycleTypes) {
        const cyclesOfType = cycles.filter((c) => c.cycle_type === type);
        if (cyclesOfType.length > 0) {
          setSelectedCycleType(type);
          setSelectedCycle(
            cyclesOfType.find((c) => c.is_active) || cyclesOfType[0],
          );
          break;
        }
      }
    }
  }, [currentCycleId, cycles, configuredCycleTypes, allowAllCycles]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const jumpToCurrent = () => {
    // Don't do anything if in "All Cycles" mode
    if (selectedCycleType === "all") return;

    // Find the current active cycle of the selected type
    const activeCycle = cycles.find(
      (c) => c.cycle_type === selectedCycleType && c.is_active,
    );
    if (activeCycle) {
      setSelectedCycle(activeCycle);
      onCycleSelect(activeCycle.id);
    }
  };

  const navigateCycle = async (direction: "prev" | "next") => {
    if (!selectedCycle || selectedCycleType === "all") return;

    const cyclesOfType = cycles
      .filter((c) => c.cycle_type === selectedCycleType)
      .sort(
        (a, b) =>
          new Date(a.start_date).getTime() - new Date(b.start_date).getTime(),
      );

    const currentIndex = cyclesOfType.findIndex(
      (c) => c.id === selectedCycle.id,
    );
    const newIndex = direction === "prev" ? currentIndex - 1 : currentIndex + 1;

    if (newIndex >= 0 && newIndex < cyclesOfType.length) {
      const newCycle = cyclesOfType[newIndex];
      setSelectedCycle(newCycle);
      onCycleSelect(newCycle.id);
    } else if (
      direction === "next" &&
      newIndex === cyclesOfType.length &&
      !creating
    ) {
      // Create a new future cycle
      try {
        setCreating(true);
        const lastCycle = cyclesOfType[cyclesOfType.length - 1];

        // Calculate next cycle dates based on cycle type
        const startDate = new Date(lastCycle.end_date);
        startDate.setDate(startDate.getDate() + 1);

        const endDate = new Date(startDate);
        let name = "";

        switch (selectedCycleType) {
          case "30-day":
            endDate.setMonth(endDate.getMonth() + 1);
            endDate.setDate(endDate.getDate() - 1);
            name = `${startDate.toLocaleString("default", { month: "long", year: "numeric" })}`;
            break;
          case "quarter": {
            endDate.setMonth(endDate.getMonth() + 3);
            endDate.setDate(endDate.getDate() - 1);
            const quarter = Math.floor(startDate.getMonth() / 3) + 1;
            name = `Q${quarter} ${startDate.getFullYear()}`;
            break;
          }
          case "year":
            endDate.setFullYear(endDate.getFullYear() + 1);
            endDate.setDate(endDate.getDate() - 1);
            name = `${startDate.getFullYear()}`;
            break;
          case "3-year":
            endDate.setFullYear(endDate.getFullYear() + 3);
            endDate.setDate(endDate.getDate() - 1);
            name = `${startDate.getFullYear()}-${endDate.getFullYear()}`;
            break;
        }

        // For years, use the generate API which is more robust
        const targetYear =
          selectedCycleType === "year"
            ? parseInt(name)
            : selectedCycleType === "3-year"
              ? parseInt(name.split("-")[0])
              : startDate.getFullYear();

        try {
          // Generate cycles for the target year
          await planningConfigApi.generateCycles(organizationId, targetYear);

          // Fetch the updated cycles
          const latestCycles =
            await planningCyclesApi.listCycles(organizationId);
          const newCycle = latestCycles.find(
            (c) => c.name === name && c.cycle_type === selectedCycleType,
          );

          if (newCycle) {
            // Found the generated cycle
            setSelectedCycle(newCycle);
            onCycleSelect(newCycle.id);
            if (onCyclesUpdate) {
              onCyclesUpdate(latestCycles);
            }
          } else {
            // If not found, try direct creation as fallback
            const createdCycle = await planningCyclesApi.createCycle(
              organizationId,
              {
                name,
                cycle_type: selectedCycleType as
                  | "quarter"
                  | "3-year"
                  | "year"
                  | "30-day",
                start_date: startDate.toISOString().split("T")[0],
                end_date: endDate.toISOString().split("T")[0],
                is_active: false,
              },
            );

            const updatedCycles = [...cycles, createdCycle];
            if (onCyclesUpdate) {
              onCyclesUpdate(updatedCycles);
            }
            setSelectedCycle(createdCycle);
            onCycleSelect(createdCycle.id);
          }
        } catch (error: any) {
          // If generation failed, check if cycle already exists
          const latestCycles =
            await planningCyclesApi.listCycles(organizationId);
          const existingCycle = latestCycles.find(
            (c) => c.name === name && c.cycle_type === selectedCycleType,
          );

          if (existingCycle) {
            // Cycle exists, just use it
            setSelectedCycle(existingCycle);
            onCycleSelect(existingCycle.id);
            if (onCyclesUpdate) {
              onCyclesUpdate(latestCycles);
            }
          } else {
            throw error; // Re-throw if we still can't find it
          }
        }
      } catch (error: any) {
        // console.error removed
        // Show more user-friendly error handling
        if (error.response?.status === 400) {
          // console.error removed
        } else if (error.code === "ERR_NETWORK") {
          // console.error removed
        }
      } finally {
        setCreating(false);
      }
    }
  };

  return (
    <div className="border border-gray-300 rounded-md p-4">
      <div className="flex items-center gap-2 mb-3">
        <CalendarIcon className="w-5 h-5 text-gray-500" />
        <span className="text-sm font-medium text-gray-700">
          Planning Cycle
        </span>
      </div>

      {/* Cycle Type Tabs */}
      <div className="flex space-x-1 mb-4">
        {/* Add "All" tab if allowed */}
        {allowAllCycles && (
          <button
            key="all"
            onClick={() => {
              setSelectedCycleType("all");
              setSelectedCycle(null);
              onCycleSelect(null);
            }}
            className={`flex-1 py-2 px-3 text-sm font-medium rounded-md transition-colors ${
              selectedCycleType === "all"
                ? "bg-blue-100 text-blue-700"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            {getCycleTypeLabel("all")}
          </button>
        )}

        {["year", "quarter", "30-day"]
          .filter((type) => configuredCycleTypes.includes(type))
          .map((type) => {
            const isActive = selectedCycleType === type;
            const cyclesOfType = cycles.filter((c) => c.cycle_type === type);
            return (
              <button
                key={type}
                onClick={() => {
                  setSelectedCycleType(type);
                  // When switching cycle types, find a cycle in the same time range if possible
                  // Otherwise default to the active cycle
                  let targetCycle: PlanningCycle | undefined;

                  if (selectedCycle) {
                    // Try to find a cycle of the new type that overlaps with current selection
                    const currentStart = new Date(selectedCycle.start_date);
                    targetCycle = cyclesOfType.find((c) => {
                      const start = new Date(c.start_date);
                      const end = new Date(c.end_date);
                      return currentStart >= start && currentStart <= end;
                    });
                  }

                  // If no overlapping cycle found, use active cycle or first available
                  if (!targetCycle) {
                    targetCycle = cyclesOfType.find((c) => c.is_active) || cyclesOfType[0];
                  }

                  if (targetCycle) {
                    setSelectedCycle(targetCycle);
                    onCycleSelect(targetCycle.id);
                  }
                }}
                disabled={cyclesOfType.length === 0}
                className={`flex-1 py-2 px-3 text-sm font-medium rounded-md transition-colors ${
                  isActive
                    ? "bg-blue-100 text-blue-700"
                    : cyclesOfType.length === 0
                      ? "bg-gray-50 text-gray-400 cursor-not-allowed"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {getCycleTypeLabel(type)}
              </button>
            );
          })}
      </div>

      {/* Cycle Navigation */}
      {selectedCycleType !== "all" ? (
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigateCycle("prev")}
            disabled={
              !selectedCycle ||
              cycles
                .filter((c) => c.cycle_type === selectedCycleType)
                .sort(
                  (a, b) =>
                    new Date(a.start_date).getTime() -
                    new Date(b.start_date).getTime(),
                )
                .findIndex((c) => c.id === selectedCycle.id) === 0
            }
            className="p-1 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeftIcon className="w-5 h-5" />
          </button>

          <div className="flex-1 text-center">
            {selectedCycle ? (
              <div>
                <div className="font-medium text-gray-900">
                  {selectedCycle.name}
                </div>
                <div className="text-sm text-gray-600">
                  {formatDate(selectedCycle.start_date)} -{" "}
                  {formatDate(selectedCycle.end_date)}
                </div>
                {selectedCycle.is_active && (
                  <span className="inline-block mt-1 px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-md">
                    Current
                  </span>
                )}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No cycle selected</p>
            )}
          </div>

          <button
            onClick={() => navigateCycle("next")}
            disabled={!selectedCycle || creating}
            className="p-1 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRightIcon className="w-5 h-5" />
          </button>
        </div>
      ) : (
        <div className="flex-1 text-center py-4">
          <p className="text-gray-700 font-medium">
            Showing all planning cycles
          </p>
          <p className="text-gray-500 text-sm mt-1">
            OKRs from all time periods are displayed
          </p>
        </div>
      )}

      {creating && (
        <div className="mt-2 text-center text-sm text-gray-600">
          Creating new cycle...
        </div>
      )}

      {/* Current button - show if not already on current cycle and not in "All" mode */}
      {(() => {
        if (selectedCycleType === "all") return null;

        const activeCycle = cycles.find(
          (c) => c.cycle_type === selectedCycleType && c.is_active,
        );
        const isOnCurrentCycle =
          activeCycle && selectedCycle?.id === activeCycle.id;

        if (activeCycle && !isOnCurrentCycle) {
          return (
            <button
              onClick={jumpToCurrent}
              className="w-full mt-2 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded py-1 px-2"
            >
              Jump to Current {getCycleTypeLabel(selectedCycleType)}
            </button>
          );
        }
        return null;
      })()}
    </div>
  );
};

export default PlanningCycleSelector;
